<template>
  <DetailRelations :data="data" :initialTab="'0'" :onDelete="handleDelete">
    <template #contractactions-body-combinedNoticePeriod="{ row }">
      <span v-if="row.noticePeriod.value" class="whitespace-nowrap">
        {{
          t(`enums.${row.noticePeriodUnit.meta('rawvalue')?.toUpperCase()}_S`, {
            count: row.noticePeriod.value,
          })
        }}
      </span>
    </template>

    <template #contractactions-body-combinedCount="{ row }">
      <span
        v-if="
          row.maxCount?.value ||
          row.type.meta('rawvalue') === ContractActionV1Dto.type.AUTOMATIC_EXTENSION
        "
        v-tooltip="{ value: t('labels.maxCount'), showDelay: 300 }"
        class="flex items-center gap-1"
      >
        <i class="pi pi-refresh"></i>
        {{ row.maxCount.value || '∞' }} / {{ row.numberOfUses?.value || 0 }}x
      </span>
    </template>

    <template #contractactions-body-combinedExtensionValue="{ row }">
      <div class="flex justify-between items-center gap-2">
        <span v-if="row.extensionValue.value">
          {{
            t(`enums.${row.extensionUnit.meta('rawvalue')?.toUpperCase()}_S`, {
              count: row.extensionValue.value,
            })
          }}
        </span>
      </div>
    </template>

    <template #conditions-body-resolvedConditionType="{ row }">
      <div>
        {{ conditionTypes.find((type) => type.id === row.conditionTypeId._meta.rawvalue)?.name }}
      </div>
    </template>

    <template #contractactions-body-status="{ row }">
      <span>
        <Tag
          :value="row.status.meta('renderedValue')"
          :severity="getStatusTagSeverity(row.status.meta('rawvalue'))"
          @click="
            (e: Event) => {
              if (
                (row.status.meta('rawvalue') === ContractActionV1Dto.status.ACTIVE &&
                  row.maxCount.meta('rawvalue') > 0) ||
                row.type.meta('rawvalue') === ContractActionV1Dto.type.AUTOMATIC_EXTENSION
              ) {
                e.stopPropagation();
                handleStatusUpdate(row, ContractActionV1Dto.status.ACTIVE);
              }
            }
          "
        />
      </span>
    </template>
    <template #contractactions-body-statusActions="{ row }">
      <div class="flex gap-4">
        <Tag
          v-if="row.status.meta('rawvalue') === ContractActionV1Dto.status.AVAILABLE"
          severity="warn"
          :value="$t('labels.activate')"
          @click="
            (e: Event) => {
              e.stopPropagation();
              handleStatusUpdate(row, ContractActionV1Dto.status.ACTIVE);
            }
          "
        />
        <Tag
          v-if="
            (row.status.meta('rawvalue') === ContractActionV1Dto.status.AVAILABLE ||
              row.status.meta('rawvalue') === ContractActionV1Dto.status.ACTIVE) &&
            row.type.meta('rawvalue') !== ContractActionV1Dto.type.TERMINATION
          "
          severity="secondary"
          :value="$t('labels.skip')"
          @click="
            (e: Event) => {
              e.stopPropagation();
              handleStatusUpdate(row, ContractActionV1Dto.status.SKIP);
            }
          "
        />
      </div>
    </template>
  </DetailRelations>
  <ModalOrPage ref="showDocumentRef">
    <template #default="{ state }">
      <DocumentsDetail :uuid="state.uuid" :inModal="true" />
    </template>
  </ModalOrPage>
  <Confirm ref="confirmDialog" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { DataTableRowClickEvent, DataTableRowDoubleClickEvent } from 'primevue';
import { useI18n } from 'vue-i18n';

import Confirm from '@/components/confirm/Confirm.vue';
import type { ModalOrPageExpose } from '@/components/details/detail-modal-or-page/DetailModalOrPage.vue';
import {
  columns as conditionColumns,
  tableConfig as conditionConfig,
} from '@/views/contracts/conditions/configs/conditionColumnConfig';
import { columns as contactPersonToContractColumns } from '@/views/contracts/contact/configs/contactPersonToContractColumnConfig';
import {
  columns as contractActionsColumns,
  createStatusPrefilterTab,
} from '@/views/contracts/contractactions/configs/contractActionColumnConfig';
import {
  useContractActions,
  useContractActionsConverter,
} from '@/views/contracts/contractactions/hooks/useContractActions';
import { columns as paymentColumns } from '@/views/contracts/payment/configs/paymentColumnConfig';
import DocumentsDetail from '@/views/documents/DocumentsDetail.vue';
import { columns as operatingCostInvoiceColumns } from '@/views/operatingcostinvoices/configs/operatingCostInvoiceColumnConfig';
import { useTableRowSortStore } from '@/stores/tableRowSort';
import { ifClickOrDblClick } from '@/utils/clickOrDblClick';
import { flattenObject } from '@/utils/object';
import { getStatusTagSeverity } from '@/utils/priority';
import type { ClauseSchemaV1Dto, PatchPositionsListV1Dto } from '@/openapi';
import { ContractActionV1Dto } from '@/openapi/models/ContractActionV1Dto';
import { eventBus } from '@/configs/eventBus';

import {
  useOperatingCostInvoices,
  useOperatingCostInvoicesConverter,
} from '../../operatingcostinvoices/hooks/useOperatingCostInvoices';
import {
  useConditions,
  useConditionsConverter,
  useConditionTypes,
} from '../conditions/hooks/useConditions';
import { documentOnContractColumns } from '../configs/contractColumnConfig';
import {
  useContactPersonsToContractConverter,
  useContactPersonToContract,
} from '../contact/hooks/useContactPersonsToContract';
import type { ContractSimple } from '../contractsTypes';
import { useContracts } from '../hooks/useContracts';
import { usePayments, usePaymentsConverter } from '../payment/hooks/usePayments';

const { t } = useI18n();
const rowSortStore = useTableRowSortStore();
const { deleteContractActions, updateContractActionStatus } = useContractActions();
const { deleteConditions } = useConditions();
const { convertContractActionDtoListToContractActionSimpleList } = useContractActionsConverter();
const { convertConditionDtoListToConditionSimpleList } = useConditionsConverter();
const { updateContractActionsOrder } = useContractActions();
const { convertContactPersonToContractDtoListToContactPersonToContractSimpleList } =
  useContactPersonsToContractConverter();
const { deleteContactPersonToContract } = useContactPersonToContract();
const { convertPaymentDtoListToPaymentSimpleList } = usePaymentsConverter();
const { fetchConditionSchemas } = useConditionTypes();
const { deleteOperatingCostInvoices } = useOperatingCostInvoices();
const { convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList } =
  useOperatingCostInvoicesConverter();
const { deletePayments } = usePayments();
const { unassignManyDocumentRelation } = useContracts();

const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);
const conditionTypes = ref<ClauseSchemaV1Dto[]>([]);
const showDocumentRef = ref<ModalOrPageExpose | null>(null);

const props = defineProps<{
  contract: ContractSimple;
  fetch: () => Promise<void>;
}>();

function showComponent(event: DataTableRowClickEvent, domain: string, edit?: boolean) {
  const id = event?.data?.id.meta('rawvalue');
  if (!id || !domain) return;
  ifClickOrDblClick(
    () => {
      /* single */
      switch (domain) {
        case 'documents':
          showDocumentRef.value?.openInModal({ uuid: id });
          break;
        default:
          eventBus.emit('goto', { path: `/${domain}/${id}${!!edit ? '/edit' : ''}` });
          break;
      }
    },
    () => {
      /* double */
      eventBus.emit('goto', { path: `/${domain}/${id}${!!edit ? '/edit' : ''}` });
    },
  );
}

const data = computed(() => [
  {
    header: { label: 'contractactions', icon: 'mdi mdi-file-document-edit-outline', value: '0' },
    table: {
      id: `contractactions-${props.contract.id}`,
      key: `contractactions`,
      data: convertContractActionDtoListToContractActionSimpleList(
        props.contract.contractAction ?? [],
      ),
      columns: contractActionsColumns,
      reorderableRows: true,
      prefilterTabs: createStatusPrefilterTab(props.contract.contractAction ?? []),
    },
  },
  {
    header: { label: 'conditions', icon: 'mdi mdi-file-document-edit-outline', value: '1' },
    table: {
      key: `conditions`,
      data: convertConditionDtoListToConditionSimpleList(props.contract.contractClauses ?? []),
      columns: conditionColumns,
      ...conditionConfig,
    },
  },
  {
    header: {
      label: 'contact',
      icon: 'mdi mdi-file-document-edit-outline',
      value: '2',
    },
    table: {
      key: `contact`,
      data: convertContactPersonToContractDtoListToContactPersonToContractSimpleList(
        props.contract.contactPersonToContract ?? [],
      ).map((x) => {
        return flattenObject(x);
      }),
      columns: contactPersonToContractColumns,
    },
  },
  {
    header: {
      label: 'payment',
      icon: 'mdi mdi-file-document-edit-outline',
      value: '3',
    },
    table: {
      key: `payment`,
      data: convertPaymentDtoListToPaymentSimpleList(props.contract.payments ?? []).map((x) => {
        return flattenObject(x);
      }),
      columns: paymentColumns,
    },
  },
  {
    header: {
      label: 'operatingcostinvoice',
      icon: 'mdi mdi-file-document-edit-outline',
      value: '4',
    },
    table: {
      key: `operatingcostinvoice`,
      data: convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList(
        props.contract.operatingCostInvoices ?? [],
      ),
      columns: operatingCostInvoiceColumns,
    },
  },
  {
    header: {
      label: 'contracttodocuments',
      value: '5',
    },
    table: {
      key: `contracttodocuments`,
      data: props.contract.documents ?? [],
      columns: documentOnContractColumns,
    },
    onRowClick: (event: DataTableRowClickEvent) => {
      showComponent(event, 'documents');
    },
    onRowDblClick: (event: DataTableRowDoubleClickEvent) => {
      showComponent(event, 'documents');
    },
  },
]);

const handleDelete = async (selectedIdsToDelete: string[], key: string) => {
  try {
    switch (key) {
      case `contractactions`:
        await deleteContractActions(selectedIdsToDelete);
        break;
      case `conditions`:
        await deleteConditions(selectedIdsToDelete);
        break;
      case `contact`:
        await deleteContactPersonToContract(selectedIdsToDelete);
        break;
      case `operatingcostinvoice`:
        await deleteOperatingCostInvoices(selectedIdsToDelete);
        break;
      case `payment`:
        await deletePayments(selectedIdsToDelete);
        break;
      case `contracttodocuments`:
        await unassignManyDocumentRelation(props.contract.id, selectedIdsToDelete);
        break;
      default:
        break;
    }
  } finally {
    await props.fetch();
  }
};

async function handleStatusUpdate(row: any, status: ContractActionV1Dto.status) {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isConfirmed();
  if (isConfirmed) {
    await updateContractActionStatus(row.id.meta('rawvalue'), {
      version: row.version.meta('rawvalue'),
      status,
    });
    await props.fetch();
  }
}

onMounted(async () => {
  conditionTypes.value = (await fetchConditionSchemas(props.contract.contractTypeId)) ?? [];

  rowSortStore.$subscribe((_mutation, state) => {
    const newPositions: PatchPositionsListV1Dto = {
      positions:
        state.rowOrders[`contractactions-${props.contract.id}`]?.map((id, position) => ({
          position,
          id,
        })) ?? [],
    };

    void updateContractActionsOrder(props.contract.id, newPositions)
      .then(() => props.fetch())
      .catch((_err) => {});
  });
});
</script>
